// 116 — Certificado de Discapacidad / Condición Discapacitante (SNS-MSP/HCU-form.116/2021, PDF oficial págs. 240-243 del visor).
// Lo que el papel imprime y esta pieza respeta:
// - B. "Discapacidad" y "Condición discapacitante" son DOS casillas independientes (instructivo: "registrar un X, si es el caso"
//   en cada una): se pueden marcar una, otra o ambas, y no hay valor por defecto. Al menos una es obligatoria para guardar.
// - E. Tratamientos recibidos: TRES filas — "Farmacológico / No farmacológico" (una sola fila), "Rehabilitación" y "Quirúrgico" —, cada
//   una con fecha de inicio y descripción. No hay casilla por fila: una fila se guarda si tiene algún dato.
// - F. Secuelas: dos casillas (Permanentes e irreversibles / Temporales y reversibles, ambas marcables) y una tabla de hasta TRES
//   tipos con su descripción. Escribir un tipo o una descripción sin marcar ninguna categoría no se guarda en silencio: se avisa.
// - NOTA impresa al pie de F sobre la condición discapacitante (secuelas temporales y reversibles, plazo mayor de un año).
import type { Traducir } from './tecnicaAnestesicaDatos';

export type ClaveTratamiento116 = 'farmacologico' | 'rehabilitacion' | 'quirurgico';

/** Las tres filas impresas de E, en su orden. */
export const CLAVES_TRATAMIENTO_116: readonly ClaveTratamiento116[] = ['farmacologico', 'rehabilitacion', 'quirurgico'];

export const NUM_SECUELAS_116 = 3;

export interface Tratamiento116 {
  readonly tipo: string;
  readonly fechaInicio: string;
  readonly descripcion: string;
}

export interface Secuela116 {
  readonly tipo: string;
  readonly descripcion: string;
}

export interface Estado116Cert {
  readonly discapacidad: boolean;
  readonly condicionDiscapacitante: boolean;
  readonly cuadroClinico: string;
  readonly tratamientos: Readonly<Record<ClaveTratamiento116, Tratamiento116>>;
  readonly permanentes: boolean;
  readonly temporales: boolean;
  readonly secuelas: readonly Secuela116[];
}

const TRATAMIENTO_VACIO: Tratamiento116 = { tipo: '', fechaInicio: '', descripcion: '' };

/** Nada marcado por defecto: el papel obliga a marcar B, y F solo si corresponde. */
export function estadoInicial116Cert(): Estado116Cert {
  return {
    discapacidad: false,
    condicionDiscapacitante: false,
    cuadroClinico: '',
    tratamientos: { farmacologico: TRATAMIENTO_VACIO, rehabilitacion: TRATAMIENTO_VACIO, quirurgico: TRATAMIENTO_VACIO },
    permanentes: false,
    temporales: false,
    secuelas: Array.from({ length: NUM_SECUELAS_116 }, () => ({ tipo: '', descripcion: '' })),
  };
}

export function tratamientoTieneDatos116(t: Tratamiento116): boolean {
  return t.tipo.trim() !== '' || t.fechaInicio !== '' || t.descripcion.trim() !== '';
}

export function secuelaTieneDatos116(s: Secuela116): boolean {
  return s.tipo.trim() !== '' || s.descripcion.trim() !== '';
}

/** Lo que impide guardar: se muestra tal cual al usuario (mismo criterio que 053: mensaje visible, nunca descarte silencioso). */
export function faltantes116Cert(e: Estado116Cert): string[] {
  const faltan: string[] = [];
  if (!e.discapacidad && !e.condicionDiscapacitante) faltan.push('Bloque B: marque Discapacidad, Condición discapacitante o ambas.');
  if (!e.permanentes && !e.temporales && e.secuelas.some(secuelaTieneDatos116)) {
    faltan.push('Bloque F: escribió el tipo o la descripción de una secuela sin marcar si es Permanente e irreversible o Temporal y reversible.');
  }
  return faltan;
}

const ETIQUETA_MARCA = { discapacidad: 'Discapacidad', condicionDiscapacitante: 'Condición discapacitante' } as const;
const ETIQUETA_CATEGORIA = { permanentes: 'Permanentes e irreversibles', temporales: 'Temporales y reversibles' } as const;

export function marcasSeleccionadas116(e: Pick<Estado116Cert, 'discapacidad' | 'condicionDiscapacitante'>): string[] {
  return [e.discapacidad ? ETIQUETA_MARCA.discapacidad : '', e.condicionDiscapacitante ? ETIQUETA_MARCA.condicionDiscapacitante : ''].filter(Boolean);
}

export function categoriasSecuela116(e: Pick<Estado116Cert, 'permanentes' | 'temporales'>): string[] {
  return [e.permanentes ? ETIQUETA_CATEGORIA.permanentes : '', e.temporales ? ETIQUETA_CATEGORIA.temporales : ''].filter(Boolean);
}

export function textoTratamiento116(t: Tratamiento116): string {
  return [t.tipo.trim(), t.fechaInicio ? `Inicio: ${t.fechaInicio}` : undefined, t.descripcion.trim()].filter(Boolean).join(' — ');
}

export interface Linea116 {
  readonly titulo: string;
  readonly texto: string;
}

/** Bloque B + C: qué se certifica y el cuadro clínico. */
export function lineasMarcaCuadro116(t: Traducir, e: Estado116Cert): Linea116[] {
  const cuadro = e.cuadroClinico.trim();
  return [{ titulo: t('formularios.f116cert.marca'), texto: marcasSeleccionadas116(e).join(' / ') }, ...(cuadro ? [{ titulo: t('formularios.f116cert.cuadroClinico'), texto: cuadro }] : [])];
}

/** Bloque E: una línea por fila con datos. */
export function lineasTratamientos116(t: Traducir, e: Estado116Cert): Linea116[] {
  return CLAVES_TRATAMIENTO_116.filter((c) => tratamientoTieneDatos116(e.tratamientos[c])).map((c) => ({
    titulo: t(`formularios.f116cert.tratamiento.${c}`),
    texto: textoTratamiento116(e.tratamientos[c]),
  }));
}

/** Bloque F: la(s) categoría(s) marcada(s) y una línea por cada tipo de secuela con datos (hasta 3, como el papel). */
export function lineasSecuelas116(t: Traducir, e: Estado116Cert): Linea116[] {
  const categorias = categoriasSecuela116(e);
  const lineas: Linea116[] = [];
  if (categorias.length > 0) lineas.push({ titulo: t('formularios.f116cert.secuelas'), texto: categorias.join(' / ') });
  // La numeración es la del renglón del papel (1-3), no la posición entre los que tienen datos.
  e.secuelas.forEach((s, i) => {
    if (secuelaTieneDatos116(s)) lineas.push({ titulo: `${t('formularios.f116cert.secuelaFila')} ${i + 1}`, texto: [s.tipo.trim(), s.descripcion.trim()].filter(Boolean).join(' — ') });
  });
  return lineas;
}

/** NOTA impresa al pie del bloque F: va en la pantalla y en el PDF. */
export function lineaNota116(t: Traducir): Linea116 {
  return { titulo: t('formularios.f116cert.notaTitulo'), texto: t('formularios.f116cert.notaTexto') };
}
